import UserMenuPanel from 'features/UserMenu/components/UserMenuPanel/UserMenuPanel';
import { Button, Form } from 'react-bootstrap';
import {
  IoCheckmarkOutline,
  IoHeartOutline,
  IoHomeOutline,
  IoKeyOutline,
  IoPersonOutline,
} from 'react-icons/io5';
import { formatAccountPhoneNumber } from '../../utils/accountProfile.utils';
import AccountField from '../AccountField/AccountField';
import '../../assets/account.scss';

/** The shared profile layout, with colors supplied by the selected appearance. */
const AccountForm = ({
  isClassic = false,
  profile,
  canEdit,
  canSave,
  error,
  message,
  onChange,
  onSave,
  onChangePassword,
}) => {
  return (
    <Form
      className="user-menu-page"
      data-appearance={isClassic ? 'classic' : 'merlin'}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) onSave();
      }}
    >
      <UserMenuPanel icon={<IoPersonOutline />} title="Information">
        <div className="row g-3">
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Name"
            value={profile.name}
            canEdit={false}
          />
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Email"
            value={profile.email}
            canEdit={false}
          />
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Employee ID"
            value={profile.employeeId}
            canEdit={false}
          />
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Phone Number"
            type="tel"
            value={profile.phoneNumber}
            canEdit={canEdit}
            onChange={(value) => onChange({ phoneNumber: formatAccountPhoneNumber(value) })}
          />
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Birthday"
            type="date"
            value={profile.dob}
            canEdit={canEdit}
            onChange={(value) => onChange({ dob: value })}
          />
          <AccountField
            classic={isClassic}
            className="col-12 col-md-6 col-xl-4"
            label="Gender"
            value={profile.gender || 'Not Specified'}
            options={['Female', 'Male', 'Not Specified']}
            canEdit={canEdit}
            onChange={(value) => onChange({ gender: value })}
          />
        </div>
      </UserMenuPanel>

      <div className="user-menu-grid" data-layout="halves">
        <UserMenuPanel icon={<IoHomeOutline />} title="Home Address">
          <div className="row g-3">
            {[
              ['street', 'Street'],
              ['city', 'City / County'],
              ['state', 'State'],
              ['zipcode', 'Zipcode'],
            ].map(([key, label]) => (
              <AccountField
                key={key}
                classic={isClassic}
                className="col-12 col-sm-6"
                label={label}
                value={profile.address?.[key]}
                canEdit={canEdit}
                onChange={(value) =>
                  onChange((previousProfile) => ({
                    ...previousProfile,
                    address: { ...previousProfile.address, [key]: value },
                  }))
                }
              />
            ))}
          </div>
        </UserMenuPanel>

        <UserMenuPanel icon={<IoHeartOutline />} title="Emergency Contact">
          <div className="d-flex flex-column gap-3">
            <AccountField
              classic={isClassic}
              label="Name"
              value={profile.emergencyContact?.name}
              canEdit={canEdit}
              onChange={(value) =>
                onChange((previousProfile) => ({
                  ...previousProfile,
                  emergencyContact: { ...previousProfile.emergencyContact, name: value },
                }))
              }
            />
            <AccountField
              classic={isClassic}
              label="Phone"
              type="tel"
              value={profile.emergencyContact?.phone}
              canEdit={canEdit}
              onChange={(value) =>
                onChange((previousProfile) => ({
                  ...previousProfile,
                  emergencyContact: {
                    ...previousProfile.emergencyContact,
                    phone: formatAccountPhoneNumber(value),
                  },
                }))
              }
            />
          </div>
        </UserMenuPanel>
      </div>

      <div className="border-top pt-3">
        <div role="status">
          {error && (
            <p className="user-menu-status mb-3" data-tone="error">
              {error}
            </p>
          )}
          {message && (
            <p className="user-menu-status mb-3" data-tone="success">
              {message}
            </p>
          )}
        </div>
        <div className="d-flex flex-column flex-sm-row justify-content-between gap-3">
          <Button
            type="button"
            variant="secondary"
            className="d-inline-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-3"
            onClick={onChangePassword}
          >
            <IoKeyOutline aria-hidden="true" />
            Change Password
          </Button>
          <Button
            type="submit"
            variant={isClassic ? 'secondary' : 'primary'}
            className="d-inline-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-3"
            disabled={!canSave}
          >
            <IoCheckmarkOutline aria-hidden="true" />
            Save
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default AccountForm;
